import React, { useMemo, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import ReactDOM from "react-dom";

import { ReactComponent as IconPlus } from "../../assets/icons/icon_plus.svg";
import { courseProgramData } from "../../config/courseProgramData";
import { useScrollLock } from "../../hooks/useScrollLock";

import {
  CLoseContainer,
  CheckIcon,
  Close,
  Content,
  Header,
  ItemContainer,
  ItemInner,
  Label,
  LeadText,
  Overlay,
  PointRow,
  PointsList,
  PopupCard,
  SectionAction,
  SectionHeader,
  SectionMeta,
  SectionTitle,
  ToggleIcon,
  WeekLabel,
} from "./styles";

export type ProgramItem = {
  id: string;
  title: string;
  subtitle?: string;
  week?: string;
  lead?: string;
  points?: string[];
};

export type ProgramSection = {
  id: string;
  label?: string;
  title: string;
  items: ProgramItem[];
};

export type CourseProgramPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  sections?: ProgramSection[];
};

export const CourseProgramPopup: React.FC<CourseProgramPopupProps> = ({
  isOpen,
  onClose,
  sections,
}) => {
  const overlayRoot = typeof document !== "undefined" ? document.getElementById("overlay-root") : null;
  const program = useMemo(() => sections ?? (courseProgramData as unknown as ProgramSection[]), [sections]);

  const [openQueue, setOpenQueue] = useState<string[]>([]);

  useScrollLock(isOpen);

  const toggleItem = (id: string) => {
    setOpenQueue((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      if (prev.length < 2) {
        return [...prev, id];
      }

      return [prev[1], id];
    });
  };

  const toggleSectionAll = (section: ProgramSection) => {
    const ids = section.items.map((i) => i.id);

    setOpenQueue((prev) => {
      const allOpen = ids.every((id) => prev.includes(id));

      if (allOpen) {
        return prev.filter((id) => !ids.includes(id));
      }
      const set = new Set([...prev, ...ids]);

      return Array.from(set);
    });
  };

  const isSectionAllOpen = (section: ProgramSection) =>
    section.items.every((i) => openQueue.includes(i.id));

  if (!isOpen || !overlayRoot) return null;

  const popup = (
    <Overlay>
      <PopupCard>
        <Header>
          <h2>Программа курса</h2>
          <CLoseContainer onClick={onClose}>
            <Close />
          </CLoseContainer>
        </Header>
        <Content>
          {program.map((section) => (
            <div key={section.id}>
              <SectionHeader>
                <div className="left">
                  {section.label && <Label>{section.label}</Label>}
                  <h3>{section.title}</h3>
                </div>
                <SectionAction onClick={() => toggleSectionAll(section)}>
                  {isSectionAllOpen(section) ? "Свернуть все разделы" : "Развернуть все разделы"}
                </SectionAction>
              </SectionHeader>
              {section.items.map((item) => {
                const isOpenItem = openQueue.includes(item.id);

                return (
                  <div key={item.id}>
                    <SectionTitle onClick={() => toggleItem(item.id)} $open={isOpenItem}>
                      <div>
                        <div className="title">{item.title}</div>
                        {item.subtitle && (
                          <SectionMeta>{item.subtitle}</SectionMeta>
                        )}
                      </div>
                      <ToggleIcon $open={isOpenItem} aria-hidden>
                        <IconPlus />
                      </ToggleIcon>
                    </SectionTitle>
                    <AnimatePresence initial={false}>
                      {isOpenItem && (
                        <motion.div
                          key={`${item.id}-content`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ type: "tween", duration: 0.25 }}
                          style={{ overflow: "hidden" }}
                        >
                          <ItemContainer>
                            <ItemInner>
                              {item.week && <WeekLabel>{item.week}</WeekLabel>}
                              {item.lead && <LeadText>{item.lead}</LeadText>}
                              {item.points && item.points.length > 0 && (
                                <PointsList>
                                  {item.points.map((text, i) => (
                                    <PointRow key={i}>
                                      <CheckIcon aria-hidden />
                                      <span>{text}</span>
                                    </PointRow>
                                  ))}
                                </PointsList>
                              )}
                            </ItemInner>
                          </ItemContainer>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          ))}
        </Content>
      </PopupCard>
    </Overlay>
  );

  return ReactDOM.createPortal(popup, overlayRoot);
};

